<template>
    <Widget :title="chartTitle" :info="$t('widgetsInfo.ChatsStats')" :loading="loading" :report-mode="reportMode">
        <v-row align="center" justify="space-between" style="width: 100%">
            <v-col cols="12" sm="4">
                <span class="d-inline-block mx-4 text-caption ddwhite--text" v-for="(item, index) in Object.values(legend)" :key="index">
                    <span class="circle" :style="{ backgroundColor: item.color }"></span> 
                    <span class="label">{{ item.title }}</span>
                </span>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.ChatsStats.FilterText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="groupBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'all', hint: $t('components.ChatsStats.AllGroupsLabel') },
                        { value: 'opened', hint: $t('components.ChatsStats.OpenedGroupLabel') },
                        { value: 'sent', hint: $t('components.ChatsStats.SentGrouoLabel') } ]"
                    :label="$t('components.ChatsStats.FilterText')"
                    @change="filterData(data)"/>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.ChatsStats.GroupText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="filterBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'month_cumulative', hint: $t('components.ChatsStats.MonthCumulativeLabel') },
                        { value: 'month', hint: $t('components.ChatsStats.MonthLabel') },
                        { value: 'day', hint: $t('components.ChatsStats.DayLabel') },
                        { value: 'hour', hint: $t('components.ChatsStats.HourLabel') } ]"
                    :label="$t('components.ChatsStats.GroupText')"
                    @change="filterData(data)"/>
            </v-col>
        </v-row>
        <StackedBarChart :stats="stats" :styles="styles"/>
    </Widget>
</template>

<script>
/** 
 * ChatsStats wraps a StackedBarChart and contains the functions to parse
 * and format data from the API.
 * 
 * It represents the number of chats versus the number of referrals grouped
 * by month, day of the week or hour of the day.
 */

import { HourRangesLabels, DayLabels } from '@/mixins/TimeLabels';

import Widget from '@/components/dashboard/Widget';

import StackedBarChart from '@/components/dashboard/charts/StackedBarChart';

export default {
    name: 'ChatsStats',
    data: () => ({ 
        stats: {},
        styles: {
            position: 'relative',
            width: '100%',
            padding: '24px 24px 0',
        },
        groupBy: 'all',
        filterBy: 'month_cumulative',
        legend: [],
    }),
    props: {
        loading: {
            type: Boolean,
            default: true
        },
        chatsData: {
            type: Object,
            required: true
        },
        reportMode: {
            type: String,
            default: null
        },
        appVersion: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        isReport() { return this.reportMode !== null },
        colors() {
            return this.$vuetify.theme.isDark ? 
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        },
        showUpselling() {
          return false;
            // const upsellClick = localStorage.getItem('upsellClicked');
            // return upsellClick !== "true";
        },
        chartTitle() {
            const baseTitle = this.$t('components.ChatsStats.Title');
            if (this.filterBy === 'month_cumulative') {
                return `${baseTitle} (Cumulative)`;
            }
            return baseTitle;
        }
    },
    mounted() {
        this.inflateLegend();
        // Cumulative is now the default, no need for feature flag check
        this.updateStats(this.chatsData);
        console.log("STATS mounted");
    },
    watch: {
        chatsData(newData) {
            this.updateStats(newData);
        },
        appVersion() {
            // Cumulative is now the default, no need for feature flag check
        },
        groupBy() {
            this.updateStats(this.chatsData);
        },
        filterBy() {
            this.updateStats(this.chatsData);
        }
    },
    methods: {
        inflateLegend() {
            const $t = this.$t.bind(this);
            this.legend = {
                chats: { 
                    title: $t("components.ChatsStats.ChatsLabel"),
                    color: this.colors.ddlightblue
                },
                referrals: { 
                    title: $t("components.ChatsStats.ReferralsLabel"),
                    color: this.colors.ddorange
                }
            }
            if(this.showUpselling)
              this.legend.projected_referrals = {
                title: $t("components.ChatsStats.AddedReferralsLabel"),
                color: "#ff9800"
              }
        },
        updateStats(data) {
            if (data) {
                const items = this.filterData(data);
                this.stats = this.formatItems(items);
            }
            switch (this.reportMode) {
                case 'daily':
                    this.filterBy = 'hour';
                    break;
                case 'weekly':
                    this.filterBy = 'day';
                    break;
                case 'monthly':
                    this.filterBy = 'month';
                    break;
            }
        },
        filterData(data) {
            if (window.GA) {
                window.GA.trackEvent('number_of_chats_filter', this.filterBy);
            }

            const $t = this.$t.bind(this);
            const items = data[this.filterBy];
            
            if (items) {
                let formatedItems = {};
                let labels = [];

                switch (this.filterBy) {
                    case 'month':
                    case 'month_cumulative':
                        labels = Object.keys(items);
                        break;
                    case 'day': 
                        labels = DayLabels.map(l => $t(`components.ChatsStats.${l}`));
                        break;
                    case 'hour':
                        labels = HourRangesLabels;
                        break;
                }

                const keys = Object.keys(items);
                const { length } = keys;
                for (let i = 0; i < length; i++) {
                    const key = keys[i];
                    const label = labels[i];
                    formatedItems[label] = items[key];
                }
                return formatedItems;
            }
            
            return {};
        },
        formatItems(items) {
            const $t = this.$t.bind(this);

            const labels = [...Object.keys(items)];
            const datasets = [];
            const opened = {
                barPercentage: 0.5,
                label: $t("components.ChatsStats.ChatsLabel"),
                backgroundColor: this.colors.ddlightblue,
                data: []
            }

            const sent = {
                barPercentage: 0.5,
                label: $t("components.ChatsStats.ReferralsLabel"),
                backgroundColor: this.colors.ddorange,
                data: []
            }

            const addedSent = {
              barPercentage: 0.5,
              label: $t("components.ChatsStats.AddedReferralsLabel"),
              backgroundColor: "#ff9800",
              data: []
            }

            const { length } = labels;
            for (let i = 0; i < length; i++) {
                const item = items[labels[i]];
                opened.data.push(item.chats);
                sent.data.push(item.referrals);
                if(this.showUpselling)
                  addedSent.data.push(Math.round(item.referrals * 1.6));
            }

            const { groupBy } = this;
            switch (groupBy) {
                case 'opened':
                    datasets.push(opened);
                    break;
                case 'sent':
                    datasets.push(sent);
                    if(this.showUpselling)
                      datasets.push(addedSent);
                    break;
                case 'all':
                    datasets.push(opened);
                    datasets.push(sent);
                    if(this.showUpselling)
                      datasets.push(addedSent);
                    break;
            }

            return { labels, datasets };
        }
    },
    components: { Widget, StackedBarChart }
}
</script>

<style>
    .circle {
        display: inline-block;
        vertical-align: top;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 4px 12px;
    }

    .label {font-size:  1.2em !important;}
    .input-label.input-label {font-size:  1.02em !important;}
    
    .groupby .v-text-field__details { display: none; }
</style>